import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    IonAlert,
    useIonViewWillLeave
} from "@ionic/react";
import Pane from "../../../components/pane/pane.component";
import {StyledButton} from "../../../components/button/button.style";
import {scanWarehouseItemQr} from "../../../services/worker.service";
import Info from "../../../components/info/info.component";
import {Capacitor} from "@capacitor/core";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    makeWebViewTransparent,
    restoreWebView,
    BARCODE_QR_FORMATS,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";

type PaneProps = {
    topEdge?: number;
    onItemScanned?: () => void;
}

const WorkerWarehouseScanPane: React.FC<PaneProps> = (props: PaneProps) => {
    const {t} = useTranslation();

    const [scanning, setScanning] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [scannedItemName, setScannedItemName] = useState<string>('');

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);

    useIonViewWillLeave(() => {
        stopScan();
    });

    const startScan = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
            return;
        }

        setScanning(true);
        makeWebViewTransparent();

        const handle = await startContinuousScan(BARCODE_QR_FORMATS, async (rawValue) => {
            if (rawValue) {
                await handleQrScanned(rawValue);
                stopScan();
            }
        });

        scanHandleRef.current = handle;
    };

    const stopScan = async () => {
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        restoreWebView();
        setScanning(false);
    };

    const handleQrScanned = async (qrCode: string) => {
        try {
            const response = await scanWarehouseItemQr(qrCode);
            if (response.data.result === 'success') {
                setScannedItemName(response.data.transfer?.warehouse_item?.name || '');
                setShowSuccessAlert(true);
                if (props.onItemScanned) {
                    props.onItemScanned();
                }
            } else {
                const message = response.data.messageKey
                    ? t(response.data.messageKey)
                    : t('workerWarehousePage.scan.error');
                setErrorMessage(message);
                setShowErrorAlert(true);
            }
        } catch (error) {
            const err = error as any;

            const messageKey = err?.response?.data?.messageKey;
            const message = messageKey
                ? t(messageKey)
                : t('workerWarehousePage.scan.error');

            setErrorMessage(message);
            setShowErrorAlert(true);
        }
    };

    return (
        <Pane topEdge={props.topEdge} marginTop={40} paddingBottom={147}>
            <Info>{t("workerWarehousePage.scan.info")}</Info>

            <div style={{padding: '20px', textAlign: 'center'}}>
                <StyledButton onClick={startScan} disabled={scanning}>
                    {scanning ? t('workerWarehousePage.scan.scanning') : t('workerWarehousePage.scan.button')}
                </StyledButton>
            </div>

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />

            <IonAlert
                isOpen={showSuccessAlert}
                onDidDismiss={() => setShowSuccessAlert(false)}
                header={t('workerWarehousePage.scan.success')}
                message={scannedItemName ? t('workerWarehousePage.scan.itemAssigned', {name: scannedItemName}) : t('workerWarehousePage.scan.itemAssignedGeneric')}
                buttons={[{text: 'OK'}]}
            />

            <IonAlert
                isOpen={showErrorAlert}
                onDidDismiss={() => setShowErrorAlert(false)}
                header={t('workerWarehousePage.scan.errorHeader')}
                message={errorMessage}
                buttons={[{text: 'OK'}]}
            />
        </Pane>
    );
};

export default WorkerWarehouseScanPane;
